import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L, { LatLngBounds, LatLngExpression } from "leaflet";
import "./Map.css";
import { FullscreenControl } from "react-leaflet-fullscreen";
import "leaflet.fullscreen/Control.FullScreen.css";
import "leaflet.fullscreen";
import myLocationIcon from "../../assets/my-location.svg";
import { FleetLabelLayer, LabelVehicle } from "./FleetLabelLayer";
import { useMapPreferences } from "../../contexts/MapPreferencesContext";

// Operational status used for marker coloring:
//   - "offline": no live socket connection; rendering last-known position
//   - "online":  connected to the backend
// Blue = connected, grey + dashed = stale.
export type VehicleStatus = "offline" | "online";

// A vehicle as the map renders it. Carries a stable `id` (declutter key) and an
// optional `confidenceRadiusM` (localization 1-sigma error, meters) used for the
// toggleable confidence range. See FleetLabelLayer.
export type MapVehicle = LabelVehicle;

interface MapProps {
  center: LatLngExpression;
  vehicles: MapVehicle[];
  bounds?: LatLngBounds;
  isLastKnownPosition?: boolean;
  shouldDisplayRecenterOnTractorButton?: boolean;
  height?: number;
}

// Renders the fleet via a single custom SVG overlay (FleetLabelLayer): no
// clustering — every vehicle is always drawn and its label decluttered so
// labels never overlap. Imperatively owns the layer and feeds it prop updates.
const LabeledMarkers = ({
  vehicles,
  showConfidence,
}: {
  vehicles: MapVehicle[];
  showConfidence: boolean;
}) => {
  const map = useMap();
  const layerRef = useRef<FleetLabelLayer | null>(null);

  useEffect(() => {
    const layer = new FleetLabelLayer({ showConfidence });
    layer.addTo(map);
    layerRef.current = layer;
    return () => {
      layer.remove();
      layerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    layerRef.current?.setVehicles(vehicles);
  }, [vehicles]);
  useEffect(() => {
    layerRef.current?.setShowConfidence(showConfidence);
  }, [showConfidence]);

  return null;
};

// Effect to handle resizing the map when its parent container size changes
const MapEffect = () => {
  const map = useMap();
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(document.getElementById("map-container")!);
    return () => resizeObserver.disconnect();
  }, [map]);

  return null;
};

// Fits the map view to the given bounds on mount
const FitBoundsEffect = ({ bounds }: { bounds: LatLngBounds }) => {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
  }, [map, bounds]);
  return null;
};

// Control to recenter the map on a given position
const RecenterControl = ({ position }: { position: L.LatLngExpression }) => {
  const map = useMap();
  useEffect(() => {
    const controlDiv = L.DomUtil.create(
      "div",
      "leaflet-bar leaflet-control leaflet-control-custom recenter-control",
    );

    controlDiv.style.backgroundImage = `url(${myLocationIcon})`;
    controlDiv.title = "Recenter on tractor";

    controlDiv.onclick = function () {
      map.flyTo(position, 15);
    };

    L.DomEvent.disableClickPropagation(controlDiv);
    const leafletTopRightContainer = map
      .getContainer()
      .querySelector(".leaflet-top.leaflet-right");
    if (leafletTopRightContainer) {
      leafletTopRightContainer.appendChild(controlDiv);
    }

    return () => {
      if (leafletTopRightContainer) {
        leafletTopRightContainer.removeChild(controlDiv);
      }
    };
  }, [map, position]);

  return null;
};

// Toggles the localization-confidence range (halos). Rendered as a Leaflet
// control button so it sits with the other on-map controls and the overlay's
// declutter treats it as an obstacle.
const ConfidenceControl = ({
  active,
  onToggle,
}: {
  active: boolean;
  onToggle: () => void;
}) => {
  const map = useMap();
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;

  useEffect(() => {
    const div = L.DomUtil.create(
      "div",
      "leaflet-bar leaflet-control leaflet-control-custom confidence-toggle",
    );
    div.setAttribute("role", "button");
    // Icon + text label so it's clear what the control does (vs. a bare glyph).
    div.innerHTML =
      '<span class="confidence-toggle-icon">◎</span>' +
      '<span class="confidence-toggle-label">Confidence</span>';
    L.DomEvent.disableClickPropagation(div);
    div.onclick = () => onToggleRef.current();
    const corner = map
      .getContainer()
      .querySelector(".leaflet-top.leaflet-right");
    // Insert as the FIRST child rather than appending: FullscreenControl (and
    // RecenterControl) also live in this corner and append last, so two
    // "append-last" controls would trade the bottom slot depending on mount
    // order. Pinning this one to the top keeps the stack order stable.
    corner?.insertBefore(div, corner.firstChild);
    return () => {
      corner?.removeChild(div);
    };
  }, [map]);

  // Reflect on/off state in the highlight, icon, label, and tooltip.
  useEffect(() => {
    const div = map
      .getContainer()
      .querySelector(".confidence-toggle") as HTMLElement | null;
    if (!div) return;
    div.classList.toggle("confidence-toggle-active", active);
    const icon = div.querySelector(".confidence-toggle-icon");
    const label = div.querySelector(".confidence-toggle-label");
    if (icon) icon.textContent = active ? "◉" : "◎";
    if (label)
      label.textContent = active ? "Confidence: on" : "Confidence: off";
    div.title = active
      ? "Localization confidence range is ON — halo radius = position uncertainty (m). Click to hide."
      : "Show localization confidence range — halo radius = position uncertainty (m).";
  }, [map, active]);

  return null;
};

const Map: React.FC<MapProps> = ({
  center,
  vehicles,
  bounds,
  isLastKnownPosition,
  shouldDisplayRecenterOnTractorButton,
  height,
}: MapProps) => {
  // Session-wide preference (shared across every <Map /> and persisted), so the
  // toggle survives navigating Dashboard → Vehicles → Dashboard and carries
  // through to the per-vehicle detail map. See MapPreferencesContext.
  const { showConfidence, toggleConfidence } = useMapPreferences();
  // Surface the toggle once ANY vehicle has reported confidence, then keep it
  // for the session so it doesn't flicker out on a poll that momentarily lacks
  // the data (e.g. a heartbeat without covariance, or a brief feed gap).
  const [confidenceSeen, setConfidenceSeen] = useState(false);
  useEffect(() => {
    if (!confidenceSeen && vehicles.some((v) => v.confidenceRadiusM != null)) {
      setConfidenceSeen(true);
    }
  }, [vehicles, confidenceSeen]);

  return (
    <div
      id="map-container"
      style={{ width: "100%", ...(height && { height: `${height}px` }) }}
    >
      <MapContainer
        center={center}
        zoom={15}
        style={{ width: "100%", height: "100%" }}
        attributionControl={false}
      >
        {isLastKnownPosition && (
          <div className="last-known-position">Showing last known position</div>
        )}
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <LabeledMarkers vehicles={vehicles} showConfidence={showConfidence} />
        <FullscreenControl
          position="topright"
          forcePseudoFullscreen
          forceSeparateButton
        />
        {confidenceSeen && (
          <ConfidenceControl
            active={showConfidence}
            onToggle={toggleConfidence}
          />
        )}
        <MapEffect />
        {bounds && <FitBoundsEffect bounds={bounds} />}
        {shouldDisplayRecenterOnTractorButton && vehicles.length > 0 && (
          <RecenterControl position={vehicles[0].position} />
        )}
      </MapContainer>
    </div>
  );
};

export default Map;
